import { useMemo, useRef, type RefObject } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Paperclip, Send, Smile, UploadCloud } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MeetingChatMessage } from "@/types/chat";

const REACTION_OPTIONS = ["+1", "yay", "heart", "lol", "eyes"];
const REACTION_LABELS: Record<string, string> = {
  "+1": "👍",
  yay: "🎉",
  heart: "❤️",
  lol: "😂",
  eyes: "👀",
};

interface MeetingChatSidebarProps {
  messages: MeetingChatMessage[];
  currentMessage: string;
  onMessageChange: (message: string) => void;
  onSendMessage: () => void;
  onSendReaction: (messageId: string, emoji: string) => void;
  onSendFile: (file: File) => Promise<void>;
  uploadingFile?: boolean;
  isOnline?: boolean;
  className?: string;
  messagesEndRef?: RefObject<HTMLDivElement>;
}

export function MeetingChatSidebar({
  messages,
  currentMessage,
  onMessageChange,
  onSendMessage,
  onSendReaction,
  onSendFile,
  uploadingFile = false,
  isOnline = true,
  className,
  messagesEndRef,
}: MeetingChatSidebarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sortedMessages = useMemo(() => {
    return [...messages].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }, [messages]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSendMessage();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await onSendFile(file);
    event.target.value = "";
  };

  return (
    <Card className={cn("flex h-full flex-col overflow-hidden rounded-[28px] border-border/70 bg-[linear-gradient(180deg,hsl(var(--card)),hsl(var(--muted)/0.16))] shadow-sm", className)}>
      <div className="flex items-center justify-between border-b border-border/70 px-4 py-4">
        <div>
          <h3 className="font-semibold text-foreground">Meeting chat</h3>
          <p className="text-xs text-muted-foreground">Real-time notes, quick reactions, and file sharing.</p>
        </div>
        {!isOnline && <Badge variant="destructive">Offline</Badge>}
      </div>

      <ScrollArea className="flex-1 px-4">
        <div className="space-y-4 py-4">
          {sortedMessages.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/70 bg-muted/20 py-10 text-center text-sm text-muted-foreground">
              No messages yet. Say hello to get things started.
            </div>
          ) : (
            sortedMessages.map((message) => {
              const displayName = message.guest_name || message.profiles?.display_name || "Guest";
              const hasAttachment = Boolean(message.attachment);

              return (
                <div key={message.id} className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{displayName}</span>
                    <span>{new Date(message.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>

                  <div
                    className={cn(
                      "space-y-3 rounded-2xl border border-border/60 bg-background/80 p-3 shadow-sm",
                      message.isPending && "opacity-70"
                    )}
                  >
                    <p className="break-words whitespace-pre-wrap text-sm text-foreground">{message.message}</p>

                    {hasAttachment && message.attachment && (
                      <a
                        href={message.attachment.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 text-sm font-medium text-primary"
                      >
                        <Paperclip className="h-4 w-4" />
                        <span className="truncate">{message.attachment.name}</span>
                        {message.attachment.size && (
                          <span className="text-xs text-muted-foreground">
                            ({Math.round(message.attachment.size / 1024)} KB)
                          </span>
                        )}
                      </a>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      {REACTION_OPTIONS.map((emoji) => {
                        const reactors = message.reactions?.[emoji] || [];
                        const count = reactors.length;

                        return (
                          <Button
                            key={`${message.id}-${emoji}`}
                            variant="outline"
                            size="sm"
                            className="h-8 rounded-full px-3 text-xs"
                            onClick={() => onSendReaction(message.id, emoji)}
                          >
                            <span className="mr-1 text-base">{REACTION_LABELS[emoji]}</span>
                            {count > 0 && <span className="text-[11px] text-muted-foreground">{count}</span>}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="space-y-3 border-t border-border/70 px-4 py-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="shrink-0 rounded-full"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFile || !isOnline}
            title="Share a file"
          >
            <UploadCloud className="h-4 w-4" />
          </Button>
          <Input
            placeholder={isOnline ? "Type a message" : "Reconnect to chat"}
            value={currentMessage}
            onChange={(e) => onMessageChange(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={!isOnline}
            className="rounded-full"
          />
          <Button
            onClick={onSendMessage}
            size="icon"
            className="rounded-full"
            disabled={!currentMessage.trim() || !isOnline}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Smile className="h-4 w-4" />
            <span>Use reactions to keep the conversation moving.</span>
          </div>
          {uploadingFile && (
            <span className="flex items-center gap-1 text-xs text-primary">
              <Paperclip className="h-3 w-3" /> Uploading...
            </span>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
      />
    </Card>
  );
}
